/**
 * @fileoverview A name we serve, turned into a pubkey (NIP-05).
 *
 * This is the lookup behind the FIRST segment of the readable URL form
 * `pages.cloistr.xyz/<author>/<slug>`, and it is deliberately the smaller half
 * of that story. The author segment also accepts an `npub` or `nprofile`, which
 * needs no lookup at all -- see `resolveReadable.ts` for why that matters more
 * than it looks.
 *
 * BARE NAMES ONLY, AGAINST OUR OWN DOMAIN. NIP-05 identifiers are ordinarily
 * written `name@domain`, and resolving that form here would turn our URL shape
 * into a proxy for anybody's `.well-known`: a link on our host would send the
 * reader's browser to a third party of the link author's choosing, and the page
 * that came back would look like ours. The design's own wording is "only works
 * for names we serve", so a name here resolves against one configured domain
 * and nothing else. Someone else's page is reached by its `naddr`, which is the
 * canonical form and needs no name service anywhere.
 *
 * EVERY FAILURE IS `null`, WITH NO REASON. A name that is not registered, a
 * name service that is down, a malformed document and a pubkey that is not
 * 64 hex characters all come back the same way. The caller turns `null` into an
 * address that names nothing, so the reader gets the app's single "not
 * available" screen rather than a second, differently-worded one that says our
 * directory is broken.
 *
 * MEASURED 2026-09-15: `https://cloistr.xyz/.well-known/nostr.json` is live and
 * answers `{"names":{}}` -- the endpoint works and NO NAMES ARE REGISTERED YET.
 * So this branch is written, unit-tested and correct, and it cannot be proved
 * against live infrastructure until identity registers its first name. That is
 * the honest state of it, and it is the reason the `npub` branch exists rather
 * than being a convenience.
 */

import { config } from '@/config/environment';

/** What a resolved name gives us: who they are, and where their events live. */
export interface ResolvedName {
  pubkey: string;
  /** From the document's `relays` map. Hints, carried into the page address. */
  relays: string[];
}

/**
 * The character set NIP-05 permits in the local part. Anything else is not a
 * name, so it is refused before a request is made rather than being sent to the
 * name service to be refused there. `@` is absent on purpose: see the header.
 */
const NAME = /^[a-z0-9._-]+$/;

const HEX_64 = /^[0-9a-f]{64}$/i;

/**
 * Resolve a bare name to a pubkey through the configured NIP-05 domain.
 *
 * `fetchImpl` is injected for tests; nothing in the app passes it.
 */
export async function resolveName(
  name: string,
  opts: { fetchImpl?: typeof fetch; timeoutMs?: number } = {}
): Promise<ResolvedName | null> {
  const local = name.trim().toLowerCase();
  if (!NAME.test(local)) return null;

  const doFetch = opts.fetchImpl ?? fetch;
  const timeoutMs = opts.timeoutMs ?? config.relayTimeoutMs;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const url = `https://${config.nip05Domain}/.well-known/nostr.json?name=${encodeURIComponent(local)}`;
    const response = await doFetch(url, { signal: controller.signal });
    if (!response.ok) return null;

    const document: unknown = await response.json();
    return read(document, local);
  } catch {
    // Offline, aborted, not JSON, or a CORS refusal. All the same answer.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Pull one name out of a NIP-05 document.
 *
 * Written against `unknown` rather than a declared response type because the
 * document comes off the public internet: a name service that starts returning
 * a number where a pubkey belongs must produce "no such name", not a crash on
 * the route that renders someone's page.
 */
function read(document: unknown, name: string): ResolvedName | null {
  if (typeof document !== 'object' || document === null) return null;

  const names = (document as { names?: unknown }).names;
  if (typeof names !== 'object' || names === null) return null;

  const pubkey = (names as Record<string, unknown>)[name];
  if (typeof pubkey !== 'string' || !HEX_64.test(pubkey)) return null;

  const lower = pubkey.toLowerCase();

  // The `relays` map is optional in the spec and is the reason this returns a
  // structure rather than a string: a name that also tells us where the author
  // publishes lets the page address we build carry hints, so the reader's very
  // first relay query goes somewhere that actually holds the page.
  const relayMap = (document as { relays?: unknown }).relays;
  const listed =
    typeof relayMap === 'object' && relayMap !== null
      ? (relayMap as Record<string, unknown>)[lower]
      : undefined;
  const relays = Array.isArray(listed)
    ? listed.filter((r): r is string => typeof r === 'string' && r.startsWith('ws'))
    : [];

  return { pubkey: lower, relays };
}
